<template>
  <div class="list" :class="pagination && !pagination.totalCount ? 'list--unpaged' : ''">

    <menu class="list__controls list__controls--spread" v-if="!hideActions || !hideSearch">
      <button class="button button--border" :class="{ 'button--hidden' : hideActions}">
        <span class="button__label">Actions</span>
      </button>

      <div class="list__menu">
        <fieldset class="list__search" v-if="!hideSearch">
          <input id="search" class="list__input" type="search" v-model="search" @input="triggerSearch($event)" placeholder="Search" />
          <label for="search" class="icon icon--search"></label>
        </fieldset>
      </div>
    </menu>

    <AdvancedSearch :searchCriterias="searchCriterias" v-if="!hideSearch" @search="triggerSearch" />

    <menu class="list__controls list__controls--spread" v-if="!hideActions || !hideCreate || !hideExport">
      <button class="button button--border" :class="{ 'button--hidden' : hideActions}">
        <span class="button__label">Actions</span>
      </button>

      <div class="list__menu">
        <button v-if="!hideCreate" data-acc="create" @click="triggerAction('create')" class="button list__button">
          <span class="button__label">{{ L.localize('Label_Create') }}</span>
        </button>

        <button v-if="!hideExport" @click="triggerAction('export', searchQueries)" class="list__button button button--light">
          <span class="button__label">{{ L.localize('Label_Export') }}
            <i class="icon icon--export"></i>
          </span>
        </button>
      </div>
    </menu>

    <ProgressBar :max="progressMax" :percent="progressValue" v-if="progressValue > 0" />

    <div class="list__content">

      <table>
        <thead class="list__header" v-if="fields.length">
          <tr class="list__row" :style="columns ? columns[i] : ''">
            <td class="list__field list__field--header" v-for="field in fields" :key="field.label">
              {{ field.label ? field.label : field.prop }}
            </td>
          </tr>
        </thead>
        <transition-group appear :name="transition" tag="tbody" class="list__body">
          <tr v-for="item in data" :key="item.id" class="list__row list__row--item">
            <template v-for="field in fields">
              <td v-if="field.html" class="list__field" v-on:click="triggerAction('detail', item, $event)" v-html="showItem(field, item)" :key="field.label"></td>
              <td v-else-if="item.__url" class="list__field-with-href" :key="field.label" :data-acc="showItem(field, item).toString().toLowerCase()">
                <router-link :to="item.__url" class="list__href">
                    {{ showItem(field, item) }}
                </router-link>
              </td>
              <td v-else class="list__field" v-on:click="triggerAction('detail', item, $event)" :key="field.label" :data-acc="showItem(field, item).toString().toLowerCase()">
                {{ showItem(field, item) }}
              </td>
            </template>
          </tr>
        </transition-group>
        <tr class="list__row list__row--item" v-if="!data.length && !isLoading">
          <td data-acc="test" class="list__field list__field--no-data" :colspan="fields.length">
            {{ L.localize("Label_NoData") }}
          </td>
        </tr>
        <tr class="list__row list__row--item" v-if="isLoading">
          <td class="list__field list__field--no-data" :colspan="fields.length">
            <spinner :lightColor="true" :contained="true" />
          </td>
        </tr>
      </table>
    </div>

    <nav v-if="pagination && pagination.totalCount && paginationRange.length > 1" class="list__controls">
      <button v-if="pagination.currentPage != 1" @click="prevPage" class="list__prev"></button>
      <span class="list__page" :class="n === pagination.currentPage ? 'list__page--active' : ''" v-for="n in paginationRange" :key="n" @click="goToPage(n)">
        {{ n }}
      </span>
      <button v-if="pagination.currentPage !== pagination.totalPage" @click="nextPage" class="list__next"></button>
    </nav>
  </div>
</template>

<script>
import L from "services/Localization";
import Util from "services/Util";
import _ from "lodash";
import { ProgressBar, Spinner } from "components/partials";
import AdvancedSearch from "components/views/members/AdvancedSearch";

export default {
  name: "list",
  components: { ProgressBar, AdvancedSearch, Spinner },
  props: {
    data: Array,
    fields: Array,
    isLoading: {
      type: Boolean,
      default: false
    },
    pagination: {
      type: Object,
      default: () => {},
      required: false
    },
    hideSearch: {
      type: Boolean,
      default: true
    },
    hideExport: {
      type: Boolean,
      default: true
    },
    hideActions: {
      type: Boolean,
      default: true
    },
    hideCreate: {
      type: Boolean,
      default: true
    },
    progressMax: {
      type: Number,
      required: false,
      default: 100
    },
    progressValue: {
      type: Number,
      required: false,
      default: 0
    }
  },
  data() {
    return {
      transition: "list--next",
      columns: null,
      visiblePages: 5,
      search: "",
      L: L,
      searchCriterias: []
    };
  },
  computed: {
    lastPage() {
      if (this.pagination.totalPage) {
        return this.pagination.totalPage;
      } else {
        return this.totalItems % this.itemsPerPage === 0
          ? this.totalItems / this.itemsPerPage
          : Math.floor(this.totalItems / this.itemsPerPage) + 1;
      }
    },
    paginationRange() {
      let start =
        this.pagination.currentPage - this.visiblePages / 2 <= 0
          ? 1
          : this.pagination.currentPage + this.visiblePages / 2 > this.lastPage
            ? Util.lowerBound(this.lastPage - this.visiblePages + 1, 1)
            : Math.ceil(this.pagination.currentPage - this.visiblePages / 2);
      let range = [];
      for (let i = 0; i < this.visiblePages && i < this.lastPage; i++) {
        range.push(start + i);
      }
      return range;
    },
    searchQueries() {
      return {
        query: this.search,
        criterias: this.searchCriterias
      };
    }
  },
  methods: {
    showItem(field, item) {
      let value = _.get(item, field.prop);
      let result = field.transform ? field.transform(value) : value;
      if (!_.isUndefined(result) && !_.isNull(result)) {
        return result;
      } else {
        return "";
      }
    },
    triggerAction(name, item, event) {
      this.$emit("action", {
        name: name,
        item: item,
        newWindow: event && event.ctrlKey
      });
    },
    triggerSearch: _.debounce(function(event) {
      this.$emit("search", this.searchQueries);
    }, 300),
    nextPage() {
      this.transition = "list--next";
      this.emitPagination(this.pagination.currentPage + 1);
    },
    prevPage() {
      this.transition = "list--prev";
      this.emitPagination(this.pagination.currentPage - 1);
    },
    goToPage(i) {
      let page = this.pagination.currentPage;
      if (i !== page) {
        this.transition = i > page ? "list--next" : "list--prev";
        this.emitPagination(i);
      }
    },
    emitPagination(page) {
      if (!this.searchQueries) {
        this.$emit("paginate", page);
      } else {
        this.$emit("paginate", page, this.searchQueries);
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// List.vue?115eb36b